
import { useState } from 'react';

export default function EmailInAction() {
  const [showReplies, setShowReplies] = useState(false);

  const email = {
    subject: 'The $0 thing that voids your fire suppression warranty',
    date: 'Tuesday, 9:14 AM',
    body: `Mike,

Quick story.

Last year, a restaurant owner called us in a panic. His hood suppression system had discharged during a grease fire. The system worked — the fire was out.

But his insurance claim was denied.

Why?

Because six months earlier, his kitchen staff had moved the fryer 18 inches to the left to make room for a new prep table.

That's it. 18 inches.

The suppression nozzles are positioned to cover specific cooking surfaces. When the equipment moves, the coverage map changes. The system is no longer protecting what it's supposed to protect.

His warranty was void. His claim was denied. His out-of-pocket cost: $34,000.

Here's what I want you to do today:

Walk into your kitchen (or your client's kitchen) and look up at the suppression nozzles. Now look at what's directly below them. Does it match what was there when the system was last inspected?

If anything has moved — fryers, griddles, woks — call us before your next inspection. A nozzle repositioning is a minor service call.

A denied insurance claim is not.

— Tom
Fireline Services

P.S. Reply to this email if you want us to do a quick coverage audit on your next visit. No charge.`
  };

  const replies = [
    {
      name: 'Dave Kowalski',
      company: 'Kowalski Restaurant Group',
      time: '11:32 AM — same day',
      avatar: 'DK',
      message: `Tom,

I just walked back there and sure enough — we added a second flat-top griddle in March and nobody told you guys. It's sitting about two feet from where the old one was.

I had no idea that was even a thing. We've had the same suppression company for 11 years and they've never once mentioned it.

Can you come out next week?

— Dave`,
      highlight: 'Booked a service call from a single email.'
    },
    {
      name: 'Linda Marsh',
      company: 'Marsh Facilities Management',
      time: '2:47 PM — same day',
      avatar: 'LM',
      message: `Tom,

Forwarding this to three of my property managers right now. We manage 14 commercial kitchens and I guarantee at least half of them have had equipment moved since their last inspection.

This is exactly the kind of thing nobody tells us about. Can you put together a one-page checklist we can give to tenants?

— Linda`,
      highlight: 'One email opened the door to 14 properties.'
    }
  ];

  return (
    <section className="py-24 bg-gray-950">
      <div className="max-w-5xl mx-auto px-6 lg:px-8">

        {/* Header */}
        <div className="text-center mb-16">
          <span className="inline-block text-red-500 text-sm font-semibold tracking-widest uppercase mb-4">
            Email In Action
          </span>
          <h2 className="text-4xl md:text-5xl font-bold text-white mb-6">
            This Is What a Daily Email Actually Does
          </h2>
          <p className="text-lg text-gray-400 max-w-2xl mx-auto">
            Not a newsletter. Not a promotion. A short, useful email that makes your customer think — and respond.
          </p>
        </div>

        {/* Email Card */}
        <div className="bg-white rounded-lg overflow-hidden shadow-2xl mb-6">
          {/* Email Client Header */}
          <div className="bg-gray-100 border-b border-gray-200 px-6 py-4">
            <div className="flex items-center gap-2 mb-3">
              <div className="w-3 h-3 rounded-full bg-red-400"></div>
              <div className="w-3 h-3 rounded-full bg-yellow-400"></div>
              <div className="w-3 h-3 rounded-full bg-green-400"></div>
            </div>
            <div className="space-y-1">
              <div className="flex items-center gap-3">
                <span className="text-xs text-gray-500 w-12">From</span>
                <span className="text-sm font-medium text-gray-800">Tom @ Fireline Services &lt;tom@firelineservices.com&gt;</span>
              </div>
              <div className="flex items-center gap-3">
                <span className="text-xs text-gray-500 w-12">To</span>
                <span className="text-sm text-gray-700">Mike Deluca &lt;mike@delucafire.com&gt;</span>
              </div>
              <div className="flex items-center gap-3">
                <span className="text-xs text-gray-500 w-12">Subject</span>
                <span className="text-sm font-bold text-gray-900">{email.subject}</span>
              </div>
              <div className="flex items-center gap-3">
                <span className="text-xs text-gray-500 w-12">Date</span>
                <span className="text-xs text-gray-500">{email.date}</span>
              </div>
            </div>
          </div>

          {/* Email Body */}
          <div className="px-8 py-8">
            <pre className="text-sm text-gray-700 leading-relaxed whitespace-pre-wrap font-sans">
              {email.body}
            </pre>
          </div>
        </div>

        {/* Toggle Replies Button */}
        {!showReplies && (
          <div className="text-center mb-10">
            <button
              onClick={() => setShowReplies(true)}
              className="inline-flex items-center gap-3 bg-red-600 hover:bg-red-700 text-white px-8 py-4 rounded-md font-semibold text-base transition-colors cursor-pointer whitespace-nowrap"
            >
              <i className="ri-mail-open-line text-lg"></i>
              See How Customers Responded
            </button>
          </div>
        )}

        {/* Replies */}
        {showReplies && (
          <div className="space-y-6 mt-10">
            <div className="flex items-center gap-4 mb-8">
              <div className="h-px flex-1 bg-gray-700"></div>
              <span className="text-gray-400 text-sm font-medium tracking-wide uppercase">Real Replies</span>
              <div className="h-px flex-1 bg-gray-700"></div>
            </div>

            {replies.map((reply, index) => (
              <div key={index} className="bg-gray-900 border border-gray-700 rounded-lg overflow-hidden">
                {/* Reply Header */}
                <div className="border-b border-gray-700 px-6 py-4 flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="w-9 h-9 flex items-center justify-center rounded-full bg-red-600 text-white text-xs font-bold">
                      {reply.avatar}
                    </div>
                    <div>
                      <p className="text-white text-sm font-semibold">{reply.name}</p>
                      <p className="text-gray-400 text-xs">{reply.company}</p>
                    </div>
                  </div>
                  <span className="text-gray-500 text-xs">{reply.time}</span>
                </div>

                {/* Reply Body */}
                <div className="px-6 py-6">
                  <pre className="text-sm text-gray-300 leading-relaxed whitespace-pre-wrap font-sans">
                    {reply.message}
                  </pre>
                </div>

                {/* Highlight Bar */}
                <div className="bg-red-600/10 border-t border-red-600/20 px-6 py-3 flex items-center gap-2">
                  <div className="w-4 h-4 flex items-center justify-center">
                    <i className="ri-arrow-right-circle-fill text-red-500 text-base"></i>
                  </div>
                  <span className="text-red-400 text-sm font-medium">{reply.highlight}</span>
                </div>
              </div>
            ))}

            {/* Bottom note */}
            <div className="text-center pt-6">
              <p className="text-gray-500 text-sm italic">
                This is what happens when your emails are useful, not promotional.
              </p>
            </div>
          </div>
        )}

      </div>
    </section>
  );
}
